import { useState, useCallback } from 'react';

export interface GeoPosition {
  latitude: number;
  longitude: number;
}

export function useGeolocation() {
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestLocation = useCallback((): Promise<GeoPosition | null> => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return Promise.resolve(null);
    }

    setLoading(true);
    setError(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          };
          setPosition(coords);
          setLoading(false);
          resolve(coords);
        },
        (err) => {
          let msg = 'Unable to get your location.';
          if (err.code === err.PERMISSION_DENIED) {
            msg = 'Location access was denied. Please enable it in your browser settings.';
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            msg = 'Location information is unavailable.';
          } else if (err.code === err.TIMEOUT) {
            msg = 'Location request timed out.';
          }
          setError(msg);
          setLoading(false);
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    });
  }, []);

  return { position, loading, error, requestLocation };
}
